import { useEffect, useState } from "react";

import { Aurora } from "./Aurora.tsx";

interface ApiStatus {
  readonly service: string;
  readonly environment: string;
  readonly version: string;
  readonly metamodel: { readonly version: string };
}

/**
 * The portal shell.
 *
 * Calls the API on mount, which is the point: a splash that renders regardless proves
 * nothing, whereas this shows whether the portal can actually reach the API through the
 * ALB. A failure is displayed rather than hidden.
 *
 * @returns The page.
 */
export function App(): React.JSX.Element {
  const [status, setStatus] = useState<ApiStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const url = import.meta.env.VITE_API_URL ?? "https://genesis-api-staging.signalhouse.io";
    const controller = new AbortController();

    fetch(url, { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((j: ApiStatus) => setStatus(j))
      .catch((e: unknown) => {
        if (e instanceof Error && e.name === "AbortError") return;
        setError(e instanceof Error ? e.message : "unreachable");
      });

    return () => controller.abort();
  }, []);

  return (
    <>
      <Aurora />
      <div className="vignette" />

      <main className="wrap">
        <div className="eyebrow">SignalHouse</div>
        <h1>GENESIS</h1>
        <div className="rule" />
        <p className="sub">
          Phase&nbsp;0 infrastructure is live. The portal is deployed from the <code>genesis</code>{" "}
          monorepo and has nothing to show yet.
        </p>
      </main>

      <footer className="meta">
        <span className="live">
          <span className={`dot ${error ? "bad" : status ? "" : "wait"}`} />
          <b>{status?.environment ?? "staging"}</b>
        </span>
        <span>
          api <b>{error ? `unreachable — ${error}` : status ? "reachable" : "checking…"}</b>
        </span>
        {status && (
          <span>
            metamodel <b>{status.metamodel.version}</b>
          </span>
        )}
        <span>
          build <b>{status?.version ?? "—"}</b>
        </span>
      </footer>
    </>
  );
}
